import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Package, Plus, ScanLine, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import ScanProductModal from "src/components/modals/ScanProductModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as purchaseActions from "src/redux/slices/purchaseSlice";
import * as supplierActions from "src/redux/slices/supplierSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import productApis from "src/api/product";
import purchaseOrderApis from "src/api/purchaseOrder";
import { currencyFormatter } from "src/utils/common";
import { formatVariantLabel } from "src/utils/common";
import { Product } from "src/types/product.types";

interface LineItemRow {
  key: number;
  product: Product | null;
  productLabel: string;
  variantId: string;
  quantity: string;
  buyingPrice: string;
  taxPercent: string;
}

let rowKeySeq = 0;
const emptyRow = (): LineItemRow => ({
  key: rowKeySeq++,
  product: null,
  productLabel: "",
  variantId: "",
  quantity: "1",
  buyingPrice: "",
  taxPercent: "",
});

const todayIso = () => new Date().toISOString().slice(0, 10);

const PurchaseForm: React.FC = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const { create, edit } = useAppSelector((s) => s.purchase);
  const { supplierList } = useAppSelector((s) => s.supplier);
  const { warehouseList } = useAppSelector((s) => s.warehouse);
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();

  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [orderDate, setOrderDate] = useState(todayIso());
  const [rows, setRows] = useState<LineItemRow[]>([emptyRow()]);
  const [touched, setTouched] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [highlightedRowKey, setHighlightedRowKey] = useState<number | null>(null);

  useEffect(() => {
    dispatch(supplierActions.getSupplierListRequest({ page: 1, limit: 100, status: "active" }));
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  useEffect(() => {
    const loadPurchaseDetails = async () => {
      if (!purchaseId) return;
      try {
        setIsLoadingDetails(true);
        const res = await purchaseOrderApis.getDetails(purchaseId);
        const order = res?.data;

        if (!order) {
          toast.error("Purchase order not found");
          navigate("/app/purchase/list");
          return;
        }
        const createdById =
          typeof order?.createdBy === "string"
            ? order.createdBy
            : order?.createdBy?._id;
        if (
          order?.orderStatus === "pending" &&
          !isTenantAdmin &&
          createdById &&
          user?._id &&
          String(createdById) !== String(user._id)
        ) {
          toast.error("Only the creator can edit this order");
          navigate("/app/purchase/list");
          return;
        }
        if (order?.orderStatus !== "pending") {
          toast.error("Only pending purchase orders can be edited");
          navigate("/app/purchase/list");
          return;
        }

        setSupplierId(order?.supplierId?._id || "");
        setWarehouseId(
          typeof order?.warehouseId === "string"
            ? order.warehouseId
            : order?.warehouseId?._id || ""
        );
        setOrderDate((order?.orderDate || order?.createdAt || todayIso()).slice(0, 10));

        const mappedRows: LineItemRow[] = (order?.products || []).map(
          (item: any) => {
            const product: Product | null =
              item.productId && typeof item.productId === "object"
                ? item.productId
                : null;
            return {
              key: rowKeySeq++,
              product,
              productLabel: product
                ? `${product.name} (${product.sku || product.productId})`
                : "",
              variantId: item.variantId || "",
              quantity: String(item.quantity ?? ""),
              buyingPrice: String(item.buyingPrice ?? ""),
              taxPercent: String(item.taxPercent ?? ""),
            };
          }
        );
        setRows(mappedRows.length ? mappedRows : [emptyRow()]);
      } catch (error) {
        console.error("Error fetching order details:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    loadPurchaseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseId, navigate, isTenantAdmin, user?._id]);

  const handleClose = () => {
    dispatch(purchaseActions.resetCreatePurchase());
    dispatch(purchaseActions.resetEditPurchase());
    navigate("/app/purchase/list");
  };

  useEffect(() => {
    if (create.data) {
      toast.success("Purchase order created successfully!");
      handleClose();
    } else if (create.error) {
      toast.error(`Error: ${create.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create]);

  useEffect(() => {
    if (edit.data) {
      toast.success("Purchase order updated successfully!");
      handleClose();
    } else if (edit.error) {
      toast.error(`Error: ${edit.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  const updateRow = (key: number, patch: Partial<LineItemRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  useEffect(() => {
    if (highlightedRowKey === null) return;
    const timer = setTimeout(() => setHighlightedRowKey(null), 1500);
    return () => clearTimeout(timer);
  }, [highlightedRowKey]);

  const applyScannedProduct = (product: Product, variantId: string) => {
    const normalizedVariantId = variantId || "";
    const existingIdx = rows.findIndex(
      (r) => r.product?._id === product._id && (r.variantId || "") === normalizedVariantId
    );

    if (existingIdx !== -1) {
      const existing = rows[existingIdx];
      updateRow(existing.key, { quantity: String((Number(existing.quantity) || 0) + 1) });
      toast.success(`Increased quantity for ${product.name}`);
      setHighlightedRowKey(existing.key);
      return;
    }

    const patch: Partial<LineItemRow> = {
      product,
      productLabel: `${product.name} (${product.sku || product.productId})`,
      variantId: normalizedVariantId,
      buyingPrice: Number(product.buyingPrice || 0) > 0 ? String(product.buyingPrice) : "",
      taxPercent: String(product.gstRate ?? 0),
    };

    const lastRow = rows[rows.length - 1];
    if (lastRow && !lastRow.product) {
      updateRow(lastRow.key, patch);
      toast.success(`Added ${product.name}`);
      setHighlightedRowKey(lastRow.key);
      return;
    }

    const newRow: LineItemRow = { ...emptyRow(), ...patch };
    setRows((prev) => [...prev, newRow]);
    toast.success(`Added ${product.name}`);
    setHighlightedRowKey(newRow.key);
  };

  const lineTotals = (row: LineItemRow) => {
    const qty = Number(row.quantity) || 0;
    const price = Number(row.buyingPrice) || 0;
    const taxPercent = Number(row.taxPercent) || 0;
    const subtotal = qty * price;
    const tax = (subtotal * taxPercent) / 100;
    return { subtotal, tax, total: subtotal + tax };
  };

  const subTotal = rows.reduce((sum, row) => sum + lineTotals(row).subtotal, 0);
  const taxTotal = rows.reduce((sum, row) => sum + lineTotals(row).tax, 0);
  const grandTotal = subTotal + taxTotal;

  const rowsValid = rows.every(
    (r) =>
      r.product?._id &&
      (r.product.productType !== "variant" || r.variantId) &&
      Number(r.quantity) > 0 &&
      Number(r.buyingPrice) > 0
  );
  const isValid = !!supplierId && !!warehouseId && rows.length > 0 && rowsValid;

  const handleSubmit = () => {
    setTouched(true);
    if (!supplierId) {
      toast.error("Please select a supplier");
      return;
    }
    if (!warehouseId) {
      toast.error(`Please select a ${warehouseLabel.toLowerCase()}`);
      return;
    }
    if (!rowsValid) {
      toast.error("Fill in every line item — product, variant (if any), quantity and cost are all required");
      return;
    }

    const products = rows.map((r) => {
      const { subtotal } = lineTotals(r);
      return {
        productId: r.product!._id,
        ...(r.product!.productType === "variant" ? { variantId: r.variantId } : {}),
        quantity: Number(r.quantity),
        buyingPrice: Number(r.buyingPrice),
        taxPercent: Number(r.taxPercent) || 0,
        totalPrice: Number(subtotal.toFixed(2)),
      };
    });

    const payload = {
      supplierId,
      warehouseId,
      orderDate,
      products,
      createdBy: user?._id || "",
    };

    if (purchaseId) {
      dispatch(purchaseActions.editPurchaseRequest({ id: purchaseId, data: payload as any }));
      return;
    }
    dispatch(purchaseActions.createPurchaseRequest(payload as any));
  };

  const pageTitle = purchaseId ? "Edit purchase order" : "Create purchase order";
  const supplierOptions = (supplierList?.data?.data || []).map((s: any) => ({
    label: `${s.name} (${s.supplierId})`,
    value: s._id,
  }));
  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  return (
    <DashboardLayout
      meta={{
        title: purchaseId ? "Edit Purchase | Invyntra" : "Create Purchase | Invyntra",
        description: "Create purchase orders.",
      }}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <Package size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{pageTitle}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Stock is added to the chosen {warehouseLabel.toLowerCase()} as soon as the order is created.
            </p>
          </div>
        </div>

        <Card>
          <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3 dark:border-gray-700">
            <span className="h-4 w-1 rounded-full bg-primary-500" />
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Receiving {warehouseLabel.toLowerCase()}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FilterSelect
                variant="boxed"
                label={warehouseLabel}
                required
                value={warehouseId}
                onChange={setWarehouseId}
                allLabel={`Select ${warehouseLabel.toLowerCase()}`}
                options={warehouseOptions}
                error={touched && !warehouseId}
                disabled={isWarehouseLocked}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isWarehouseLocked
                  ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                  : `Stock is added to this ${warehouseLabel.toLowerCase()} as soon as the order is created.`}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3 dark:border-gray-700">
            <span className="h-4 w-1 rounded-full bg-primary-500" />
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Order details</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FilterSelect
                variant="boxed"
                label="Supplier"
                required
                value={supplierId}
                onChange={setSupplierId}
                allLabel="Select supplier"
                options={supplierOptions}
                error={touched && !supplierId}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Goods will be received from this supplier.
              </p>
            </div>
            <Input
              label="Order date"
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />
          </div>
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Products</h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {rows.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                aria-label="Scan barcode"
                onClick={() => setScanOpen(true)}
              >
                <ScanLine size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={addRow}>
                <Plus size={14} className="mr-1" />
                Add Product
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="grid grid-cols-[36px_2fr_1.4fr_80px_100px_80px_100px_100px_36px] gap-2 px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <span />
                <span>Product</span>
                <span>Variant</span>
                <span>Qty</span>
                <span>Cost</span>
                <span>Tax %</span>
                <span>Tax</span>
                <span>Total</span>
                <span />
              </div>

              <div className="space-y-2">
                {rows.map((row, idx) => {
                  const isVariantProduct = row.product?.productType === "variant";
                  const variantOptions = (row.product?.variants || []).map((v) => ({
                    label: formatVariantLabel(v.optionValues) || v.sku || "Variant",
                    value: v._id || "",
                    disabled: rows.some(
                      (r) =>
                        r.key !== row.key &&
                        r.product?._id === row.product?._id &&
                        r.variantId === v._id
                    ),
                  }));
                  const { tax, total } = lineTotals(row);
                  const rowInvalid =
                    touched &&
                    (!row.product?._id ||
                      (isVariantProduct && !row.variantId) ||
                      !(Number(row.quantity) > 0) ||
                      !(Number(row.buyingPrice) > 0));

                  const isHighlighted = row.key === highlightedRowKey;

                  return (
                    <div
                      key={row.key}
                      className={`grid grid-cols-[36px_2fr_1.4fr_80px_100px_80px_100px_100px_36px] items-center gap-2 rounded-xl border p-2 transition-colors ${
                        isHighlighted
                          ? "border-primary-300 bg-primary-50/60 ring-2 ring-primary-300 dark:border-primary-700 dark:bg-primary-900/20 dark:ring-primary-700"
                          : rowInvalid
                          ? "border-red-200 dark:border-red-900/60"
                          : "border-gray-100 dark:border-gray-700"
                      }`}
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                        {idx + 1}
                      </span>

                      <SearchInput<Product>
                        placeholder="Search product..."
                        value={row.productLabel}
                        onSearch={async (query) => {
                          const res = await productApis.getProductList({
                            page: 1,
                            limit: 10,
                            status: "active",
                            search: query,
                          });
                          const list: Product[] = res?.data?.data || res?.data || [];
                          return list.map((p) => ({
                            value: p._id || "",
                            label: `${p.name} (${p.sku || p.productId})`,
                            data: p,
                          }));
                        }}
                        onSelect={(option: SearchInputOption<Product>) =>
                          updateRow(row.key, {
                            product: option.data,
                            productLabel: option.label,
                            variantId: "",
                            buyingPrice:
                              Number(option.data.buyingPrice || 0) > 0
                                ? String(option.data.buyingPrice)
                                : row.buyingPrice,
                            taxPercent: String(option.data.gstRate ?? row.taxPercent ?? 0),
                          })
                        }
                        onClear={() =>
                          updateRow(row.key, {
                            product: null,
                            productLabel: "",
                            variantId: "",
                          })
                        }
                        renderOption={(option) => (
                          <div>
                            <div className="font-medium text-gray-800 dark:text-gray-100">
                              {option.data.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              SKU: {option.data.sku || "-"} · HSN:{" "}
                              {option.data.hsnCode || "-"}
                            </div>
                          </div>
                        )}
                      />

                      {isVariantProduct ? (
                        <div className="[&_label]:hidden">
                          <FilterSelect
                            variant="boxed"
                            label="Variant"
                            value={row.variantId}
                            onChange={(val) => updateRow(row.key, { variantId: val })}
                            allLabel="Select"
                            options={variantOptions}
                            error={touched && !row.variantId}
                          />
                        </div>
                      ) : (
                        <span className="text-center text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}

                      <Input
                        type="text"
                        inputClassName="text-center"
                        value={row.quantity}
                        onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                      />
                      <Input
                        type="text"
                        placeholder="Required"
                        inputClassName="text-center"
                        value={row.buyingPrice}
                        onChange={(e) => updateRow(row.key, { buyingPrice: e.target.value })}
                      />
                      <Input
                        type="text"
                        inputClassName="text-center"
                        value={row.taxPercent}
                        onChange={(e) => updateRow(row.key, { taxPercent: e.target.value })}
                      />

                      <span className="text-center text-sm text-gray-600 dark:text-gray-300">
                        {currencyFormatter(tax)}
                      </span>
                      <span className="text-center text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {currencyFormatter(total)}
                      </span>

                      <button
                        type="button"
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length === 1}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-red-600 hover:bg-red-50 disabled:opacity-30 dark:text-red-400 dark:hover:bg-red-900/30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Card className="w-full sm:w-80">
            <h2 className="mb-3 text-sm font-semibold text-gray-800 dark:text-gray-200">
              Order summary
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Subtotal</span>
                <span>{currencyFormatter(subTotal)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-2 text-base font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100">
                <span>Total</span>
                <span>{currencyFormatter(grandTotal)}</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={create.loading || edit.loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={create.loading || edit.loading || isLoadingDetails}
            disabled={isLoadingDetails || (touched && !isValid)}
          >
            {purchaseId ? "Save changes" : "Create purchase order"}
          </Button>
        </div>
      </div>

      <ScanProductModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onProductFound={applyScannedProduct}
      />
    </DashboardLayout>
  );
};

export default PurchaseForm;
